import { gql, useApolloClient, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { UserRoleSelect } from 'src/app/account/membersPage/roleSelect/UserRoleSelect';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

export const UpdateGraphRoleModalMembershipFragment = gql`
  fragment UpdateGraphRoleModalMembershipFragment on AccountMembership {
    createdAt
    role: permission
    user {
      id
      fullName
    }
    account {
      id
    }
  }
`;

export const UpdateGraphRoleModal = ({
  isOpen,
  graphId,
  membership,
  isSelf,
  onClose,
  graphRole = membership.role,
  availableRoles,
}: {
  isOpen: boolean;
  graphId: string;
  membership: GraphQLTypes.UpdateGraphRoleModalMembershipFragment;
  isSelf: boolean;
  onClose: () => void;
  graphRole: GraphQLTypes.UserPermission | undefined;
  availableRoles: GraphQLTypes.UserPermission[];
}) => {
  const client = useApolloClient();
  const [updateRole, { data, error, loading }] = useMutation<
    GraphQLTypes.UpdateGraphRoleMutation,
    GraphQLTypes.UpdateGraphRoleMutationVariables
  >(gql`
    mutation UpdateGraphRoleMutation(
      $graphId: ID!
      $userID: ID!
      $role: UserPermission
    ) {
      service(id: $graphId) {
        overrideUserPermission(userID: $userID, permission: $role) {
          id
          roleOverrides {
            role
            user {
              id
            }
          }
        }
      }
    }
  `);

  // call onCancel if the mutation is done and there are no errors / not loading
  // we know the data prop will be populated after the mutation is done, not before
  if (data && !error) {
    onClose();
  }

  const [newUserRole, setNewUserRole] = useState(
    !Config.roleIsGraphApiKeyOnly(graphRole) &&
      Config.orderedUserOverrideRoles.includes(graphRole)
      ? graphRole
      : GraphQLTypes.UserPermission.GRAPH_ADMIN,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader className="z-[2]">
          <ModalTitle>
            What role should {membership.user.fullName} have for this graph?
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {isSelf && (
            <Alert status="warning">
              You may not be able to reverse this change.
            </Alert>
          )}
          <UserRoleSelect
            target="user-on-graph"
            userRole={newUserRole}
            setUserRole={setNewUserRole}
            options={Config.orderedUserOverrideRoles}
            availableRoles={availableRoles}
          />
          {!!error && (
            <ErrorMessage className="mb-10">
              {error?.message ?? 'forced storybook error'}
            </ErrorMessage>
          )}
        </ModalBody>
        <ModalFooter>
          <Link isExternal to={DocsRouter.path('GraphRoles')}>
            Learn more about roles
          </Link>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={async () => {
                await updateRole({
                  variables: {
                    graphId,
                    userID: membership.user.id,
                    role: newUserRole,
                  },
                });

                if (isSelf) {
                  await client.resetStore();
                }
              }}
              isLoading={loading}
              loadingText="Update"
            >
              Update
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
